import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import { useSearchParams } from "@/hooks/router/use-search-params";
import { MealPlan } from "@/pages/plans/types/meal-plan.type";
import { useIonRouter } from "@ionic/react";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";

export const planDetailMealQueryKey = [QueryKeys.PLAN_DETAIL_MEAL] as const;

const readPlanDetailDataFromStorage = (): MealPlan | null => {
  const mealData = localStorage.getItem(LocalStorageKeys.PLAN_DETAIL_MEAL_DATA);
  if (!mealData) {
    return null;
  }
  try {
    return JSON.parse(mealData) as MealPlan;
  } catch (error) {
    console.error("Failed to parse meal detail data:", error);
    return null;
  }
};

const writePlanDetailDataToStorage = (mealPlan: MealPlan | null) => {
  if (!mealPlan) {
    localStorage.removeItem(LocalStorageKeys.PLAN_DETAIL_MEAL_DATA);
    return;
  }
  localStorage.setItem(LocalStorageKeys.PLAN_DETAIL_MEAL_DATA, JSON.stringify(mealPlan));
};

export const setPlanDetailMealData = (queryClient: QueryClient, mealPlan: MealPlan | null) => {
  queryClient.setQueryData(planDetailMealQueryKey, mealPlan);
  writePlanDetailDataToStorage(mealPlan);
};

export const useMealDetailData = () => {
  const navigate = useIonRouter();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: planDetailMealQueryKey,
    queryFn: readPlanDetailDataFromStorage,
    initialData: readPlanDetailDataFromStorage,
    staleTime: Infinity,
  });

  const getMealDetailData = (): MealPlan | null => data ?? null;

  const onMealDetailClick = (mealPlan: MealPlan, saveSearchParams: boolean = true) => {
    if (!mealPlan) {
      return;
    }
    setPlanDetailMealData(queryClient, mealPlan);

    if (saveSearchParams) {
      // Convert URLSearchParams to an object, then to JSON string
      const searchParamsObject = Object.fromEntries(searchParams.entries());
      const searchParamsJson = JSON.stringify(searchParamsObject);
      localStorage.setItem(LocalStorageKeys.SWAP_PLAN_SEARCH_PARAMS, searchParamsJson);
    }

    navigate.push("/plans/meal-detail", "forward", "push");
  };

  const clearMealDetailData = () => {
    setPlanDetailMealData(queryClient, null);
  };

  return {
    onMealDetailClick,
    getMealDetailData,
    clearMealDetailData,
  };
};
